import { useNavigate } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { BriefcaseBusiness, RadioIcon } from 'lucide-react'
import { useOrganizationMinimal, useOrganizationsMinimal, useSelectedOrganizationId } from '@/organization/api'
import { OrganizationSelectorInline } from '@/components/OrganizationSelectorInline'
import { StepFrame } from '@/components/installFlow/StepFrame'
import { StepFooter } from '@/components/installFlow/StepFooter'
import { Card } from '@/components/ui/card'
import { useInstallFlowNavigation } from '@/installFlow/useInstallFlowNavigation'

/**
 * Inner component that fetches and displays organization selector
 * Separated to isolate the suspending organization queries
 */
function IntroContent({
                        onOrganizationSelect
}: {
  onOrganizationSelect: (organization: any) => void
}) {
  const { data: { results: allOrganizations } } = useOrganizationsMinimal()
  const organizationId = useSelectedOrganizationId()
  const { data: selectedOrganization } = useOrganizationMinimal(organizationId)

  const hasMultipleOrgs = allOrganizations.length > 1

  return (
    <div className="flex flex-col gap-4 items-center justify-center">
      {/* Header */}
      <div className="flex flex-col space-y-2 items-center justify-center text-center">
        <div className="w-fit aspect-square rounded-lg bg-thalo-blueLight p-4 flex justify-center items-center">
          <RadioIcon className="text-white" size={48}/>
        </div>
        <h1 className="text-2xl font-semibold">Let's bring your Thalo sensor online</h1>
        <div className="text-muted-foreground space-y-2">
          <p>If the sensor is already installed on the HVAC equipment, you’re in the right place.</p>
          <p>We’ll guide you through mapping it to the correct property and confirming it’s streaming data to Thalo Copilot.</p>
        </div>
      </div>

      {/* Organization Selector */}
      {hasMultipleOrgs && (
        <Card className="space-y-3 p-4 border-t border-b">
          <div className="flex items-center gap-2 text-sm font-medium">
            <BriefcaseBusiness className="h-4 w-4"/>
            <span>Installing for: {selectedOrganization.name}</span>
          </div>
          <div className="text-sm text-muted-foreground mb-2">
            Switch organization:
          </div>
          <OrganizationSelectorInline
            organizations={allOrganizations}
            selectedOrganization={selectedOrganization}
            onSelect={onOrganizationSelect}
          />
        </Card>
      )}
    </div>
  )
}

export function IntroStep() {
  const navigate = useNavigate()
  const { goToStep } = useInstallFlowNavigation()

  const handleGetStarted = () => {
    goToStep('scan')
  }

  const handleOrganizationSelect = (organization: any) => {
    navigate({
      to: '/organization/$organizationId/install-flow',
      params: { organizationId: organization.id },
      search: (prev) => ({
        ...prev,
        step: 'intro',
      }),
    })
  }

  return (
    <StepFrame
      main={
        <IntroContent
          onOrganizationSelect={handleOrganizationSelect}
        />
      }
      footer={
        <StepFooter
          actions={[
            <Button key="get-started" onClick={handleGetStarted} variant="thalo" className="w-full" size="lg">
              Get started
            </Button>
          ]}
        />
      }
    />
  )
}
